<template>
  <el-dialog
    :model-value="visible"
    :title="form.Codigo ? 'Editar Atributo' : 'Nuevo Atributo'"
    width="600px"
    @update:model-value="$emit('update:visible', $event)"
    @close="resetForm"
  >
    <el-form
      ref="formRef"
      :model="form"
      :rules="rules"
      label-position="top"
    >
      <el-form-item
        label="Etiqueta"
        prop="Etiqueta"
      >
        <el-input
          :model-value="form.Etiqueta"
          placeholder="Ej: Forma de Plegado"
          @update:model-value="updateField('Etiqueta', $event)"
        />
      </el-form-item>

      <el-form-item
        label="Clave"
        prop="Clave"
        :error="errorClaveDuplicada"
      >
        <el-input
          :model-value="form.Clave"
          placeholder="Ej: forma_plegado o forma-plegado"
          :disabled="!!form.Codigo"
          @update:model-value="updateField('Clave', $event)"
          @input="errorClaveDuplicada = ''"
        />
        <template #extra>
          <small class="text-muted">
            La clave no puede modificarse después de crear el atributo. No use espacios ni comience
            con números.
          </small>
        </template>
      </el-form-item>

      <el-form-item
        label="Descripción"
        prop="Descripcion"
      >
        <el-input
          :model-value="form.Descripcion"
          type="textarea"
          :rows="3"
          placeholder="Descripción del atributo"
          @update:model-value="updateField('Descripcion', $event)"
        />
      </el-form-item>

      <el-row :gutter="20">
        <el-col :span="12">
          <el-form-item
            label="Tipo"
            prop="Tipo"
          >
            <el-select
              :model-value="form.Tipo"
              style="width: 100%"
              :disabled="!!form.Codigo"
              @update:model-value="updateField('Tipo', $event)"
            >
              <el-option
                label="Texto"
                value="string"
              />
              <el-option
                label="Número"
                value="number"
              />
              <el-option
                label="Booleano"
                value="boolean"
              />
              <el-option
                label="Fecha"
                value="date"
              />
              <el-option
                label="Selección Simple"
                value="select"
              />
              <el-option
                label="Selección Múltiple"
                value="multiselect"
              />
            </el-select>
          </el-form-item>
        </el-col>
      </el-row>

      <!-- Sección de Opciones para Select/MultiSelect -->
      <template v-if="form.Tipo === 'select' || form.Tipo === 'multiselect'">
        <el-divider />

        <OpcionesSection
          :model-value="form.Opciones"
          :tipo="form.Tipo"
          @update:model-value="updateField('Opciones', $event)"
        />

        <el-divider />
      </template>

      <el-form-item label="Valor por Defecto">
        <!-- Select/MultiSelect con opciones dinámicas -->
        <el-select
          v-if="form.Tipo === 'select' || form.Tipo === 'multiselect'"
          :model-value="form.ValorPorDefecto"
          placeholder="Seleccione valor por defecto (opcional)"
          :multiple="form.Tipo === 'multiselect'"
          clearable
          style="width: 100%"
          :disabled="!form.Opciones?.length"
          @update:model-value="updateField('ValorPorDefecto', $event)"
        >
          <el-option
            v-for="opcion in form.Opciones"
            :key="opcion.Clave || opcion.Etiqueta"
            :label="opcion.Etiqueta"
            :value="opcion.Clave || opcion.Etiqueta"
          />
        </el-select>

        <!-- Otros tipos de campo -->
        <component
          :is="getComponentForTipo(form.Tipo)"
          v-else
          :model-value="form.ValorPorDefecto"
          placeholder="Valor por defecto (opcional)"
          style="width: 100%"
          @update:model-value="updateField('ValorPorDefecto', $event)"
        />

        <template
          v-if="(form.Tipo === 'select' || form.Tipo === 'multiselect') && !form.Opciones?.length"
          #extra
        >
          <small class="text-muted"
            >Agregue opciones primero para seleccionar un valor por defecto</small
          >
        </template>
      </el-form-item>
    </el-form>

    <template #footer>
      <el-button @click="$emit('update:visible', false)">Cancelar</el-button>
      <el-button
        type="primary"
        :loading="saving"
        @click="handleSave"
      >
        Guardar
      </el-button>
    </template>
  </el-dialog>
</template>

<script setup>
import {
  ref,
  watch,
} from 'vue';
import OpcionesSection from './OpcionesSection.vue';

const props = defineProps({
  visible: {
    type: Boolean,
    required: true,
  },
  form: {
    type: Object,
    required: true,
  },
  saving: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:visible', 'save', 'update:form']);

const formRef = ref(null);
const errorClaveDuplicada = ref('');

const rules = {
  Etiqueta: [{ required: true, message: 'La etiqueta es requerida', trigger: 'blur' }],
  Clave: [
    { required: true, message: 'La clave es requerida', trigger: 'blur' },
    {
      pattern: /^[a-zA-Z_][a-zA-Z0-9_-]*$/,
      message: 'La clave no puede tener espacios ni comenzar con un número',
      trigger: 'blur',
    },
  ],
  Tipo: [{ required: true, message: 'El tipo es requerido', trigger: 'change' }],
};

function updateField(field, value) {
  emit('update:form', { ...props.form, [field]: value });
}

function resetForm() {
  errorClaveDuplicada.value = '';
  formRef.value?.clearValidate();
}

async function handleSave() {
  try {
    await formRef.value.validate();
    emit('save', errorClaveDuplicada.value);
  } catch (error) {
    // Error de validación
  }
}

function getComponentForTipo(tipo) {
  const components = {
    string: 'el-input',
    number: 'el-input-number',
    boolean: 'el-switch',
    date: 'el-date-picker',
  };
  return components[tipo] || 'el-input';
}

watch(
  () => props.visible,
  (newVal) => {
    if (!newVal) {
      resetForm();
    }
  },
);
</script>

<style scoped>
.text-muted {
  color: #909399;
  font-size: 14px;
}
</style>
